<template>
    <div class="container">
        <div class="row mt-2 mb-4">
            <div class="col-12 col-lg-7 col-xl-6">
                <form class="calendar-search" role="search" @submit="submit">
                    <label for="calendar-order-search" class="visually-hidden">{{$t('requisition.search_by_fields')}}</label>
                    <div class="calendar-search__control d-flex align-items-center">
                        <BootstrapIcon class="calendar-search__icon flex-shrink-0" name="search" :size="19" />
                        <input
                        id="calendar-order-search"
                        v-model="filters.search"
                        type="search"
                        class="calendar-search__input flex-fill border-0 bg-transparent"
                        :placeholder="$t('requisition.search_by_fields')"
                        :aria-label="$t('requisition.search_by_fields')"
                        autocomplete="off"
                        >
                        <button
                        v-if="filters.search.length > 0"
                        type="button"
                        class="calendar-search__clear flex-shrink-0 border-0"
                        :aria-label="$t('requisition.clear_search')"
                        :title="$t('requisition.clear_search')"
                        @click="filters.search = ''"
                        >
                            <BootstrapIcon name="x-lg" :size="15" />
                        </button>
                    </div>
                </form>
            </div>
        </div>
        <div v-if="loaded" class="row mb-4">
            <div class="col">
                <div v-if="showEmptyState" class="calendar-empty d-flex align-items-center gap-2 mb-4 px-3 py-3">
                    <BootstrapIcon color="fill-pear-700 text-pear-700" :name="showSearchEmptyState ? 'search' : 'calendar2-check'" :size="20" />
                    <span v-if="showSearchEmptyState" class="fw-bolder">{{$t('requisition.no_orders_for_search')}}</span>
                    <span v-else class="fw-bolder">{{$t('requisition.no_orders_for_today')}}</span>
                </div>
                <section
                v-for="(items, key0) in orders"
                :key="key0"
                class="calendar-day"
                :class="{'calendar-day--today': key0 === today}"
                >
                    <header class="calendar-day__header d-flex align-items-center justify-content-between gap-3 px-3 py-3">
                        <div class="d-flex align-items-center gap-3 min-w-0">
                            <span class="calendar-day__icon flex-shrink-0">
                                <BootstrapIcon name="calendar3" :size="18" />
                            </span>
                            <div class="min-w-0">
                                <span v-if="key0 === today" class="calendar-day__today d-block small fw-bolder mb-1">{{$t('requisition.today')}}</span>
                                <time :datetime="key0" class="calendar-day__date d-block fw-bolder">{{format_date_parts(key0)}}</time>
                            </div>
                        </div>
                        <span class="calendar-day__count flex-shrink-0 fw-bolder">
                            <template v-if="items.length === 1">1 {{$t('delivery.order')}}</template>
                            <template v-else>{{items.length}} {{$t('delivery.orders')}}</template>
                        </span>
                    </header>
                    <div class="calendar-day__orders p-2 p-md-3">
                        <div v-for="(order, indexO) in items" :key="indexO" class="calendar-day__order">
                            <OrderWidget :orderIndex="key0+indexO" :order="order" :filters_search="filters.search" :business_details="business_details" />
                        </div>
                    </div>
                </section>
            </div>
        </div>

        <ModalConfirmEmailReceipt :order="selected_order" />
        <ModalConfirmDiscardOrder :order="selected_order" />
    </div>
</template>

<script>
import OrderWidget from '@/common/directive/OrderWidget.vue'
import BootstrapIcon from '@/common/directive/BootstrapIcon.vue'
import RequisitionStore from '@/common/store/requisition.js'
import InventoryOrderStore from '@/common/store/inventory_order.js'
import DateFormat from 'date-fns/format'
import { enUS, ro } from 'date-fns/locale'
import ModalConfirmEmailReceipt from '@/common/modal/modal-confirm_email_receipt.vue'
import ModalConfirmDiscardOrder from '@/common/modal/modal-confirm_discard_order.vue'

export default {
    components: {
        OrderWidget,
        BootstrapIcon,
        ModalConfirmDiscardOrder,
        ModalConfirmEmailReceipt
    },
    data: function () {
        let today = new Date()
        today = today.getFullYear() + '-' + (today.getMonth()+1).toString().padStart(2, '0') + '-' + today.getDate().toString().padStart(2, '0')
        return {
            STATUS_PENDING: 'order_pending',
            STATUS_PREPARING: 'order_preparing',
            STATUS_COMPLETED: 'order_completed',
            STATUS_DELIVERED: 'order_delivered',
            STATUS_DISCARDED: 'order_discarded',
            orders: [],
            filters: {
                search: ''
            },
            today,
            selected_order: null,
            business_details: null,
            loaded: false,
            timeout: 0
        }
    },
    computed: {
        location: function () {
            return RequisitionStore.state.location
        },
        locale: function () {
            return this.$i18n.locale
        },
        hasActiveSearch: function () {
            return typeof this.filters.search === 'string' && this.filters.search.length >= 2
        },
        hasOrders: function () {
            if (!this.orders || typeof this.orders !== 'object') return false
            return Object.values(this.orders).some(items => Array.isArray(items) && items.length > 0)
        },
        showSearchEmptyState: function () {
            return this.hasActiveSearch && !this.hasOrders
        },
        showEmptyState: function () {
            if (this.showSearchEmptyState) return true
            return !this.hasActiveSearch && typeof this.orders[this.today] === 'undefined'
        }
    },
    watch: {
        filters: {
            deep: true,
            handler: function () {
                clearTimeout(this.timeout)

                const self = this
                this.timeout = setTimeout(async function () {
                    await self.get_calendar()
                }, 250)
            }
        },
        location: async function () {
            await this.get_calendar()
        }
    },
    created: async function () {
        const self = this

        if (this.location && this.location.id) {
            await this.get_calendar()
        }

        this.$bus.$off('Order/email_receipt')
        this.$bus.$on('Order/email_receipt', function (order) {
            self.selected_order = order
            self.$bus.$emit('Modal/show/confirm-email-receipt')
        })

        this.$bus.$off('Order/discard_order')
        this.$bus.$on('Order/discard_order', function (order) {
            self.selected_order = order
            self.$bus.$emit('Modal/show/confirm-order-discard')
        })

        this.$bus.$off('Order/status_handed')
        this.$bus.$on('Order/status_handed', function () {
            self.get_calendar()
        })
        this.$bus.$off('Order/status_discarded')
        this.$bus.$on('Order/status_discarded', function () {
            self.get_calendar()
        })
    },
    methods: {
        submit: async function ($event) {
            $event.preventDefault()
        },
        get_calendar: async function () {
            // const self = this
            const query = {
                location_id: this.location.id
                // from_h: this.filters.from_h,
                // status: Object.keys(this.filters.status).filter(function (key) {
                //     return self.filters.status[key]
                // })
            }
            if (this.filters.search.length >= 2) {
                query.search = this.filters.search
            }
            const response = await InventoryOrderStore.dispatch('GET_CALENDAR', query)
            if (response.data.status === 0) {
                this.orders = response.data.orders
                this.business_details = response.data.business_details
            } else {
                this.$bus.$emit('Notification/show', { title: this.$t('notifications.something_went_wrong'), msg: this.$t('notifications.please_retry_the_operation_or_refresh'), level: 'danger' })
            }

            this.loaded = true
        },
        format_date_parts: function (str) {
            const parts = str.split('-')
            return DateFormat(new Date(parts[0], parts[1]-1, parts[2]), 'dd MMM yyyy', { locale: this.locale === 'ro' ? ro : enUS })
        }
    }
}
</script>
